import React, { useState, useMemo } from 'react';
import { countries as countriesJSON } from 'data';
import { VQ } from '../../../../../store/student/types';
import {
  DateInputWithConfirm,
  InputWithConfirm,
  AutocompleteInputWithConfirm,
  getStringFromDate,
  getDateFromString,
  ContinueButton,
  isValidDate,
} from 'shared-components';

import { IDate, IOtherUkVisas } from '../../../../../types';

interface IProps {
  index: number;

  visaType: string;
  issueDate: string;
  expiryDate: string;
  country: string;
  issuingAuthority: string;

  onSaveAndClose: (visa: IOtherUkVisas) => void;
}

const OtherUkVisas = (props: IProps) => {
  const { index, onSaveAndClose } = props;
  const [visaType, setVisaType] = useState(props.visaType);
  const [dIssue, setDIssue] = useState(() => getDateFromString(props.issueDate));
  const [dExpiry, setDExpiry] = useState(() => getDateFromString(props.expiryDate));
  const [dIssueError, setDIssueError] = useState('');
  const [dExpiryError, setDExpiryError] = useState('');
  const [country, setCountry] = useState(props.country);
  const [issuingAuthority, setIssuingAuthority] = useState(props.issuingAuthority);

  const countries = useMemo(() => [...new Set(countriesJSON.map(country => country.countryName))], []);
  const [countrySuggestions, setCountrySuggestions] = useState(countries);

  const saveAndClose = () => {
    onSaveAndClose({
      visaType,
      issueDate: getStringFromDate(dIssue),
      expiryDate: getStringFromDate(dExpiry),
      country,
      issuingAuthority,
    });
  };

  const handleOnSaveAndCloseClick = (_event: React.MouseEvent<HTMLButtonElement>) => {
    saveAndClose();
  };

  const validateDate = (dateValue: IDate): string => {
    const { YYYY, DD, MM } = dateValue;

    if (YYYY && DD && MM) {
      const isValid = isValidDate(dateValue);
      if (!isValid) {
        return "Doesn't look like a valid date";
      }
      return '';
    }

    return '';
  };

  const onBlurCountry = (event: React.FormEvent<HTMLInputElement>) => {
    const value = (event.currentTarget.value || '').trim();
    const country = countries.includes(value) ? value : '';

    setCountry(country);
    setCountrySuggestions(countries);
  };

  const onCountryChange = (evt: any) => {
    const value = evt.currentTarget.value || '';
    const suggestions = countries.filter((country: string) => country.toLowerCase().includes(value.toLowerCase()));

    setCountry(value);
    setCountrySuggestions(suggestions);
  };

  const complete =
    visaType !== '' &&
    dIssue.DD !== null &&
    dIssue.MM !== null &&
    dIssue.YYYY !== null &&
    dExpiry.DD !== null &&
    dExpiry.MM !== null &&
    dExpiry.YYYY !== null &&
    dIssueError === '' &&
    dExpiryError === '' &&
    country !== '' &&
    issuingAuthority !== '';

  return (
    <>
      <InputWithConfirm
        key={`${VQ.OTHER_UK_VISA_TYPE}-${index}`}
        id={`${VQ.OTHER_UK_VISA_TYPE}-${index}`}
        value={visaType}
        label={`Type of visa`}
        onChange={(evt: any) => {
          setVisaType(evt.currentTarget.value);
        }}
      />
      <DateInputWithConfirm
        key={`${VQ.OTHER_UK_VISA_DATE_OF_ISSUE}-${index}`}
        id={`${VQ.OTHER_UK_VISA_DATE_OF_ISSUE}-${index}`}
        label={`Date of issue`}
        value={dIssue}
        errorText={dIssueError}
        onChangeDD={(evt: any) => setDIssue({ ...dIssue, DD: evt.target.value })}
        onChangeMM={(evt: any) => setDIssue({ ...dIssue, MM: evt.target.value })}
        onChangeYY={(evt: any) => setDIssue({ ...dIssue, YYYY: evt.target.value })}
        onBlur={(_evt: any) => setDIssueError(validateDate(dIssue))}
      />
      <DateInputWithConfirm
        key={`${VQ.OTHER_UK_VISA_DATE_OF_EXPIRY}-${index}`}
        id={`${VQ.OTHER_UK_VISA_DATE_OF_EXPIRY}-${index}`}
        label={`Date of expiry`}
        value={dExpiry}
        errorText={dExpiryError}
        onChangeDD={(evt: any) => setDExpiry({ ...dExpiry, DD: evt.target.value })}
        onChangeMM={(evt: any) => setDExpiry({ ...dExpiry, MM: evt.target.value })}
        onChangeYY={(evt: any) => setDExpiry({ ...dExpiry, YYYY: evt.target.value })}
        onBlur={(_evt: any) => setDExpiryError(validateDate(dExpiry))}
      />
      <AutocompleteInputWithConfirm
        key={`${VQ.OTHER_UK_VISA_COUNTRY}-${index}`}
        id={`${VQ.OTHER_UK_VISA_COUNTRY}-${index}`}
        label={`Country / Territory`}
        onChange={(evt: any) => onCountryChange(evt)}
        value={country}
        suggestions={countrySuggestions}
        onBlur={(evt: any) => onBlurCountry(evt)}
      />
      <InputWithConfirm
        key={`${VQ.OTHER_UK_VISA_ISSUING_AUTH}-${index}`}
        id={`${VQ.OTHER_UK_VISA_ISSUING_AUTH}-${index}`}
        value={issuingAuthority}
        label={`Issuing authority`}
        onChange={(evt: any) => {
          setIssuingAuthority(evt.currentTarget.value);
        }}
      />

      <ContinueButton index={index} handleOnSaveAndCloseClick={handleOnSaveAndCloseClick} complete={complete} />
    </>
  );
};

export default OtherUkVisas;
